import React, { useState } from 'react';
import { Star, Clock, ChefHat, Calendar, User as UserIcon, Edit2, X, Save, ArrowLeft, Tag, Check } from 'lucide-react';
export type DetailedRecipe = {
  id: string;
  thumbnail: string;
  title: string;
  rating: number;
  description: string;
  prepTime: string;
  cookTime: string;
  difficulty: string;
  createdBy: string;
  createdOn: string;
  defaultServings: number;
  ingredients: Array<{
    item: string;
    amount: number;
    unit: string;
  }>;
  instructions: string[];
  storage: string;
  reheating: string;
};
interface RecipeDetailViewProps {
  recipe: DetailedRecipe;
  onDiscard: () => void;
  onSave: () => void;
  isLoading: boolean;
  onFinish?: () => void;
  showFinishButton?: boolean;
}
export function RecipeDetailView({
  recipe,
  onDiscard,
  onSave,
  isLoading,
  onFinish,
  showFinishButton = false
}: RecipeDetailViewProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [servings, setServings] = useState(recipe.defaultServings);
  const [editedTitle, setEditedTitle] = useState(recipe.title);
  const [editedDescription, setEditedDescription] = useState(recipe.description);
  const [editedInstructions, setEditedInstructions] = useState(recipe.instructions);
  const servingMultiplier = servings / recipe.defaultServings;
  const recipeTags = [recipe.difficulty, recipe.prepTime.replace(' ', ''), `${recipe.defaultServings}servings`, 'Balanced', 'Family-Friendly'];
  const handleSave = () => {
    setIsEditing(false);
    onSave();
  };
  return <div className="w-full border-2 border-gray-800 bg-white flex flex-col" data-id="element-207">
      {/* Header with Actions */}
      <div className="border-b-2 border-gray-200 p-3 md:p-4 flex items-center justify-between bg-gray-50 gap-2" data-id="element-208">
        <button onClick={onDiscard} className="flex items-center space-x-2 text-xs font-mono text-gray-600 hover:text-gray-900 border border-gray-300 px-2 md:px-3 py-2 hover:border-gray-500 transition-colors flex-shrink-0" data-id="element-209">
          <ArrowLeft className="w-3 h-3" data-id="element-210" />
          <span className="hidden sm:inline" data-id="element-211">Back to Options</span>
          <span className="sm:hidden" data-id="element-212">Back</span>
        </button>

        <div className="flex items-center space-x-2" data-id="element-213">
          {showFinishButton && onFinish && <button onClick={onFinish} className="flex items-center space-x-2 text-xs font-mono text-white bg-gray-900 hover:bg-gray-800 px-3 md:px-4 py-2 transition-colors" data-id="element-214">
              <Check className="w-3 h-3" data-id="element-215" />
              <span className="hidden sm:inline" data-id="element-216">Finish & Publish</span>
              <span className="sm:hidden" data-id="element-217">Finish</span>
            </button>}

          {isEditing ? <>
              <button onClick={() => setIsEditing(false)} className="flex items-center space-x-2 text-xs font-mono text-gray-600 hover:text-gray-900 border border-gray-300 px-2 md:px-3 py-2" data-id="element-218">
                <X className="w-3 h-3" data-id="element-219" />
                <span className="hidden sm:inline" data-id="element-220">Cancel</span>
              </button>
              <button onClick={handleSave} className="flex items-center space-x-2 text-xs font-mono text-white bg-gray-900 hover:bg-gray-800 px-2 md:px-3 py-2" data-id="element-221">
                <Save className="w-3 h-3" data-id="element-222" />
                <span className="hidden sm:inline" data-id="element-223">Save Recipe</span>
                <span className="sm:hidden" data-id="element-224">Save</span>
              </button>
            </> : <button onClick={() => setIsEditing(true)} className="flex items-center space-x-2 text-xs font-mono text-gray-600 hover:text-gray-900 border border-gray-300 px-2 md:px-3 py-2 hover:border-gray-500 transition-colors" data-id="element-225">
              <Edit2 className="w-3 h-3" data-id="element-226" />
              <span className="hidden sm:inline" data-id="element-227">Edit</span>
            </button>}
        </div>
      </div>

      {/* Recipe Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6 max-h-[600px]" data-id="element-228">
        {/* Header Section - RESPONSIVE */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-6" data-id="element-229">
          {/* Thumbnail - Centered on mobile */}
          <div className="flex-shrink-0 mx-auto md:mx-0" data-id="element-230">
            <div className="w-32 h-32 border-2 border-gray-300 bg-gray-100 flex items-center justify-center text-6xl" data-id="element-231">
              {recipe.thumbnail}
            </div>
          </div>

          {/* Title & Meta - Full width on mobile */}
          <div className="flex-1 space-y-3" data-id="element-232">
            {isEditing ? <input type="text" value={editedTitle} onChange={e => setEditedTitle(e.target.value)} className="w-full text-2xl md:text-3xl font-bold text-gray-900 border-2 border-gray-300 px-3 py-2 focus:border-gray-500 focus:outline-none" data-id="element-233" /> : <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight" data-id="element-234">
                {editedTitle}
              </h2>}

            {/* Rating */}
            <div className="flex items-center space-x-1" data-id="element-235">
              {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < recipe.rating ? 'fill-gray-800 text-gray-800' : 'text-gray-300'}`} data-id="element-236" />)}
              <span className="ml-2 text-sm font-mono text-gray-500" data-id="element-237">
                {recipe.rating}.0
              </span>
            </div>

            {/* Description */}
            {isEditing ? <textarea value={editedDescription} onChange={e => setEditedDescription(e.target.value)} rows={2} className="w-full text-sm text-gray-600 border-2 border-gray-300 px-3 py-2 focus:border-gray-500 focus:outline-none" data-id="element-238" /> : <p className="text-sm text-gray-600 leading-relaxed" data-id="element-239">
                {editedDescription}
              </p>}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 pt-3 border-t border-gray-200" data-id="element-240">
          {recipeTags.map((tag, idx) => <span key={idx} className="inline-flex items-center px-2 py-1 border border-gray-300 bg-white text-xs font-mono text-gray-600" data-id="element-241">
              {tag}
            </span>)}
        </div>

        {/* Meta Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 p-3 md:p-4 border-2 border-dashed border-gray-200 bg-gray-50" data-id="element-242">
          <div className="space-y-1" data-id="element-243">
            <div className="flex items-center space-x-1 text-gray-400" data-id="element-244">
              <Clock className="w-3 h-3" data-id="element-245" />
              <span className="font-mono text-xs uppercase" data-id="element-246">Prep</span>
            </div>
            <p className="text-sm font-bold text-gray-800" data-id="element-247">{recipe.prepTime}</p>
          </div>
          <div className="space-y-1" data-id="element-248">
            <div className="flex items-center space-x-1 text-gray-400" data-id="element-249">
              <Clock className="w-3 h-3" data-id="element-250" />
              <span className="font-mono text-xs uppercase" data-id="element-251">Cook</span>
            </div>
            <p className="text-sm font-bold text-gray-800" data-id="element-252">{recipe.cookTime}</p>
          </div>
          <div className="space-y-1" data-id="element-253">
            <div className="flex items-center space-x-1 text-gray-400" data-id="element-254">
              <ChefHat className="w-3 h-3" data-id="element-255" />
              <span className="font-mono text-xs uppercase" data-id="element-256">Level</span>
            </div>
            <p className="text-sm font-bold text-gray-800" data-id="element-257">
              {recipe.difficulty}
            </p>
          </div>
          <div className="space-y-1" data-id="element-258">
            <div className="flex items-center space-x-1 text-gray-400" data-id="element-259">
              <UserIcon className="w-3 h-3" data-id="element-260" />
              <span className="font-mono text-xs uppercase" data-id="element-261">By</span>
            </div>
            <p className="text-sm font-bold text-gray-800 truncate" data-id="element-262">
              {recipe.createdBy}
            </p>
          </div>
          <div className="space-y-1" data-id="element-263">
            <div className="flex items-center space-x-1 text-gray-400" data-id="element-264">
              <Calendar className="w-3 h-3" data-id="element-265" />
              <span className="font-mono text-xs uppercase" data-id="element-266">Date</span>
            </div>
            <p className="text-sm font-bold text-gray-800" data-id="element-267">
              {recipe.createdOn}
            </p>
          </div>
        </div>

        {/* Ingredients with Portion Calculator */}
        <div className="space-y-4" data-id="element-268">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-269">
            <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-270">
              Ingredients
            </h3>
            <div className="flex items-center space-x-3" data-id="element-271">
              <span className="font-mono text-xs text-gray-500" data-id="element-272">Servings:</span>
              <div className="flex items-center border border-gray-300" data-id="element-273">
                <button onClick={() => setServings(Math.max(1, servings - 1))} className="px-3 py-1 border-r border-gray-300 hover:bg-gray-100 font-mono text-sm" data-id="element-274">
                  −
                </button>
                <span className="px-4 py-1 font-mono text-sm font-bold" data-id="element-275">
                  {servings}
                </span>
                <button onClick={() => setServings(servings + 1)} className="px-3 py-1 border-l border-gray-300 hover:bg-gray-100 font-mono text-sm" data-id="element-276">
                  +
                </button>
              </div>
            </div>
          </div>

          <ul className="space-y-2 border border-gray-200 p-3 md:p-4 bg-white" data-id="element-277">
            {recipe.ingredients.map((ingredient, idx) => <li key={idx} className="flex items-start space-x-3 text-sm" data-id="element-278">
                <span className="font-mono text-gray-300 select-none w-6 flex-shrink-0" data-id="element-279">
                  {(idx + 1).toString().padStart(2, '0')}
                </span>
                <span className="text-gray-700" data-id="element-280">
                  <strong className="font-mono" data-id="element-281">
                    {(ingredient.amount * servingMultiplier).toFixed(1)}{' '}
                    {ingredient.unit}
                  </strong>{' '}
                  {ingredient.item}
                </span>
              </li>)}
          </ul>
        </div>

        {/* Instructions */}
        <div className="space-y-4" data-id="element-282">
          <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-283">
            Preparation Instructions
          </h3>

          {isEditing ? <div className="space-y-2" data-id="element-284">
              {editedInstructions.map((instruction, idx) => <div key={idx} className="flex gap-2" data-id="element-285">
                  <span className="font-mono text-xs text-gray-400 pt-2 flex-shrink-0" data-id="element-286">
                    {idx + 1}.
                  </span>
                  <textarea value={instruction} onChange={e => {
              const updated = [...editedInstructions];
              updated[idx] = e.target.value;
              setEditedInstructions(updated);
            }} rows={2} className="flex-1 border-2 border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none" data-id="element-287" />
                </div>)}
            </div> : <ol className="space-y-4 border border-gray-200 p-3 md:p-4 bg-white" data-id="element-288">
              {editedInstructions.map((instruction, idx) => <li key={idx} className="flex gap-3 text-sm" data-id="element-289">
                  <span className="font-mono text-gray-400 font-bold flex-shrink-0" data-id="element-290">
                    {(idx + 1).toString().padStart(2, '0')}.
                  </span>
                  <p className="text-gray-700 leading-relaxed" data-id="element-291">{instruction}</p>
                </li>)}
            </ol>}
        </div>

        {/* Storage & Reheating */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-292">
          <div className="space-y-2 p-3 md:p-4 border border-gray-200 bg-gray-50" data-id="element-293">
            <h4 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-294">
              Storage Instructions
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-295">
              {recipe.storage}
            </p>
          </div>
          <div className="space-y-2 p-3 md:p-4 border border-gray-200 bg-gray-50" data-id="element-296">
            <h4 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-297">
              Reheating Instructions
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-298">
              {recipe.reheating}
            </p>
          </div>
        </div>
      </div>
    </div>;
}